/**
 * AI Support Platform integration — canonical URL/deep-link contract.
 *
 * Every support link MUST include `app=birdman` (without it the portal defaults
 * to HerdManager) and carry the tenant via the `solutionId` query param. The
 * support origin is build-time public config (`VITE_SUPPORT_URL`).
 *
 * See the integration contract for the stable URL shapes:
 *   {supportBaseUrl}/?solutionId={orgId}&app=birdman[&topic=suggest|report]
 */
import { getInstanceInfo } from './instance';

/** Canonical production support origin (no trailing slash). */
export const DEFAULT_SUPPORT_BASE_URL = 'https://support-prod-three.vercel.app';

/** The product slug must always be sent explicitly. */
export const SUPPORT_PRODUCT = 'birdman';

export type SupportTopic = 'help' | 'suggest' | 'report';

const trimEnd = (value: string): string => value.replace(/\/+$/, '');

/** Build-time support origin; falls back to the canonical production origin. */
export function getSupportBaseUrl(): string {
  const env = import.meta.env as Record<string, string | undefined>;
  const configured = env.VITE_SUPPORT_URL?.trim();
  return trimEnd(configured || DEFAULT_SUPPORT_BASE_URL);
}

/**
 * Tenant/org identifier carried as `solutionId` in links and `tenant_key` in
 * APIs. Prefers an explicit `VITE_SOLUTION_ID`, else the deployment instance id.
 */
export function getOrgId(): string {
  const env = import.meta.env as Record<string, string | undefined>;
  const explicit = env.VITE_SOLUTION_ID?.trim();
  if (explicit) return explicit;
  const instanceId = getInstanceInfo().id;
  return instanceId || 'birdman';
}

export interface SupportUrlOptions {
  topic?: SupportTopic;
  /** Optional analytics params (appended only when provided). */
  platform?: 'android' | 'web' | 'ios';
  version?: string;
  locale?: string;
  /** Overrides (mainly for tests / the /support redirect passthrough). */
  orgId?: string;
  baseUrl?: string;
}

/**
 * Build a stable support portal URL. `topic` is omitted for help (the portal
 * defaults to help). `orgId` is URL-encoded. Total length is kept < 2048.
 */
export function buildSupportUrl(options: SupportUrlOptions = {}): string {
  const base = trimEnd(options.baseUrl?.trim() || getSupportBaseUrl());
  const orgId = options.orgId?.trim() || getOrgId();

  const params = new URLSearchParams();
  // solutionId first for human readability; param order is not significant.
  params.set('solutionId', orgId);
  params.set('app', SUPPORT_PRODUCT);
  if (options.topic && options.topic !== 'help') params.set('topic', options.topic);
  if (options.platform) params.set('platform', options.platform);
  if (options.version) params.set('version', options.version);
  if (options.locale) params.set('locale', options.locale);

  const url = `${base}/?${params.toString()}`;
  return url.length > 2048 ? url.slice(0, 2048) : url;
}

/** Convenience: the three canonical actions, with default web analytics. */
export function getSupportLinks(): Record<SupportTopic, string> {
  const version = getInstanceInfo().version;
  const common = { platform: 'web' as const, version };
  return {
    help: buildSupportUrl({ topic: 'help', ...common }),
    suggest: buildSupportUrl({ topic: 'suggest', ...common }),
    report: buildSupportUrl({ topic: 'report', ...common }),
  };
}
